import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SecurityIcon from '@mui/icons-material/Security';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PaletteIcon from '@mui/icons-material/Palette';
import ReorderIcon from '@mui/icons-material/Reorder';
import { useNavigate } from 'react-router-dom';
import { DocumentDesignCustomizer } from '../components/DocumentDesignCustomizer';
import { MenuOrderCustomizer } from '../components/MenuOrderCustomizer';
import { TIMEZONE_OPTIONS } from '../../../shared/utils/dateUtils';
import {
  useGetCurrentCompanyQuery,
  useUpdateCurrentCompanyMutation,
  useUpdateCompanyLogoMutation,
  useTestPrinterConnectionMutation,
} from '../../../store/api/companyApi';
import qzService from '../../../shared/utils/qzService';
import { useGetRolesQuery, useGetPermissionsQuery } from '../../../store/api/roleApi';
import { 
  useGetEmailTemplatesQuery, 
  useCreateEmailTemplateMutation, 
  useUpdateEmailTemplateMutation, 
  useDeleteEmailTemplateMutation 
} from '../../../store/api/emailTemplateApi';
import { usePermission } from '../../../hooks/usePermission';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
const CURRENCIES = [
  'USD',
  'EUR',
  'GBP',
  'AED',
  'SAR',
  'QAR',
  'OMR',
  'BHD',
  'KWD',
  'INR',
  'MYR',
  'THB',
  'SGD',
  'AUD',
  'CAD',
  'CHF',
  'JPY',
];

const TAX_LABELS = ['None', 'VAT', 'GST', 'Tax', 'Sales Tax'];

const SALES_TEMPLATES = [
  { id: 'custom', label: '🎨 Custom Design Studio (Configured)', description: 'Fully customized layout, branding colors, column toggles, terms, and bank details from Design Studio.' },
  { id: 'standard', label: 'Template 1 – Standard A4', description: 'Full invoice layout for normal A4 printing.' },
  { id: 'thermal', label: 'Template 2 – Thermal', description: 'Narrow thermal receipt style layout.' },
  { id: 'template3', label: 'Template 3 – Red Geometric Modern', description: 'Premium modern layout with red-and-slate geometric accents, customer details box, bank details, and signature area.' },
  { id: 'template4', label: 'Template 4', description: 'Compact A4 layout (reserved for future use).' },
  { id: 'template5', label: 'Template 5', description: 'Minimal invoice (reserved for future use).' },
  { id: 'template6', label: 'Template 6', description: 'Detailed invoice (reserved for future use).' },
  { id: 'template7', label: 'Template 7', description: 'Retail style layout (reserved for future use).' },
  { id: 'template8', label: 'Template 8', description: 'Service invoice layout (reserved for future use).' },
  { id: 'template9', label: 'Template 9', description: 'Landscape A4 layout (reserved for future use).' },
  { id: 'template10', label: 'Template 10', description: 'Custom layout (reserved for future use).' },
  { id: 'template11', label: 'Template 11 – Tax Invoice Grid', description: 'Grid-style tax invoice with signature area (based on uploaded sample).' },
  { id: 'a5', label: 'Template A5 – Tax Invoice (Compact Receipt)', description: 'Compact tax invoice layout for A5 portrait pages with centered headers, solid grid lines, and PAID watermark.' },
  { id: 'a4_preprinted', label: 'Template A4 – Pre-printed Paper', description: 'Dynamic body only, no header/footer/watermark. For use with pre-printed A4 letterheads.' },
  { id: 'a5_preprinted', label: 'Template A5 – Pre-printed Paper', description: 'Dynamic body only, no header/footer/watermark. For use with pre-printed A5 letterheads.' },
];

const ALLOWED_TEMPLATE_IDS = SALES_TEMPLATES.map((t) => t.id);

const ITEM_NAME_FORMAT_OPTIONS = [
  { value: 'name', label: 'Name only' },
  { value: 'name_sku', label: 'Name + SKU' },
  { value: 'name_brand', label: 'Name + Brand' },
  { value: 'name_brand_sku', label: 'Name + Brand + SKU' },
  { value: 'brand_name', label: 'Brand + Name' },
  { value: 'brand_name_sku', label: 'Brand + Name + SKU' },
  { value: 'name_desc', label: 'Name + Description' },
  { value: 'name_desc_sku', label: 'Name + Description + SKU' },
  { value: 'name_brand_desc', label: 'Name + Brand + Description' },
  { value: 'name_brand_desc_sku', label: 'Name + Brand + Description + SKU' },
  { value: 'desc', label: 'Description only' },
  { value: 'desc_sku', label: 'Description + SKU' },
  { value: 'desc_name', label: 'Description + Name' },
];

const ALLOWED_ITEM_FORMAT_IDS = ITEM_NAME_FORMAT_OPTIONS.map((o) => o.value);

const defaultConfig = {
  name: '',
  nameArabic: '',
  website: '',
  email: '',
  phone: '',
  address: '',
  currency: 'AED',
  currencyDecimals: 2,
  taxId: '',
  vatRate: '',
  vatNumber: '',
  taxLabel: 'VAT',
  salesVatIncluded: true,
  purchaseVatIncluded: true,
  creditDaysOptions: '7,14,30',
  maxDiscountType: '', // '' = no limit, 'percent' | 'amount'
  maxDiscountPercent: '',
  maxDiscountAmount: '',
  invoicePrefix: 'INV',
  invoiceNextNumber: '1',
  invoiceNumberPadding: '6',
  printPaperType: 'a4', // 'a4' | 'a5' | 'thermal'
  thermalPaperWidth: '80', // 58 | 80
  printItemNameFormat: 'name_sku', // name | name_sku | name_brand | name_brand_sku | brand_name | brand_name_sku
  showWarrantyOnInvoice: true,
  salesPrintTemplate: 'standard',
  quotationPrintTemplate: 'standard',
  dateFormat: 'dd/MM/yyyy',
  timezone: 'Asia/Dubai',
  smtpHost: '',
  smtpPort: '587',
  smtpUser: '',
  smtpPass: '',
  smtpEncryption: 'tls',
  mailFromAddress: '',
  mailFromName: '',
  whatsappProvider: 'none',
  whatsappInstanceId: '',
  whatsappToken: '',
  whatsappPhoneNumberId: '',
  whatsappAccessToken: '',
  barcodePrintShowCompanyName: true,
  showWholesalePrice: true,
  enableSalesSession: false,
  salesSessionMode: 'shared',
  salesVisibilityScope: 'all',
  enableServiceModule: false,
  defaultCustomerId: '',
  costPriceCodeMapping: '',
  barcodeAfterScanBehavior: 'search',
  barcodeAfterScanBehaviorSales: 'search',
  barcodeAfterScanBehaviorPurchase: 'quantity',
  duplicateItemBehaviorSales: 'dialog',
  duplicateItemBehaviorPurchase: 'dialog',
  enableQuickScanPrice: true,
  enableNetworkThermalPrinter: false,
  networkPrinterIp: '',
  networkPrinterPort: '9100',
  enableDirectPrintSilently: false,
  enableNegativeStockSales: false,
  stayOnSalesFormAfterSave: false,
  stockValuationMethod: 'LPC',
  enableAutoPrintOnSave: false,
  enableQzPrint: false,
  qzPrinterName: '',
  qzBarcodePrinterName: '',
};

function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const [config, setConfig] = useState(defaultConfig);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [systemSaveSuccess, setSystemSaveSuccess] = useState(false);
  const [qzPrintersList, setQzPrintersList] = useState([]);
  const [searchingQzPrinters, setSearchingQzPrinters] = useState(false);
  const navigate = useNavigate();

  const { data: company, isLoading: loadingCompany } = useGetCurrentCompanyQuery();
  const [updateCompany, { isLoading: saving }] = useUpdateCurrentCompanyMutation();
  const { data: customersResponse } = useGetCustomersQuery({ limit: 1000 });
  const customers = customersResponse?.data || (Array.isArray(customersResponse) ? customersResponse : []);
  const [updateCompanyLogo, { isLoading: uploadingLogo }] = useUpdateCompanyLogoMutation();
  const [testPrinter, { isLoading: testingPrinter }] = useTestPrinterConnectionMutation();

  const logoInputRef = useRef(null);
  const { data: roles } = useGetRolesQuery();
  const { data: allPermissions } = useGetPermissionsQuery();
  const { isAdmin, hasPermission: userHasPerm } = usePermission();

  useEffect(() => {
    if (company) {
      setConfig({
        name: company.name ?? '',
        nameArabic: company.nameArabic ?? '',
        website: company.website ?? '',
        email: company.email ?? '',
        phone: company.phone ?? '',
        address: company.address ?? '',
        currency: company.currency ?? 'AED',
        currencyDecimals: company.currencyDecimals ?? 2,
        taxId: company.taxId ?? '',
        vatRate: company.vatRate != null ? String(company.vatRate) : '',
        vatNumber: company.vatNumber ?? '',
        taxLabel: company.taxLabel ?? 'VAT',
        salesVatIncluded: company.salesVatIncluded !== false,
        purchaseVatIncluded: company.purchaseVatIncluded !== false,
        creditDaysOptions: company.creditDaysOptions ?? '7,14,30',
        maxDiscountType: company.maxDiscountType === 'percent' || company.maxDiscountType === 'amount' ? company.maxDiscountType : '',
        maxDiscountPercent: company.maxDiscountPercent != null ? String(company.maxDiscountPercent) : '',
        maxDiscountAmount: company.maxDiscountAmount != null ? String(company.maxDiscountAmount) : '',
        invoicePrefix: company.invoicePrefix ?? 'INV',
        invoiceNextNumber: company.invoiceNextNumber != null ? String(company.invoiceNextNumber) : '1',
        invoiceNumberPadding: company.invoiceNumberPadding != null ? String(company.invoiceNumberPadding) : '6',
        printPaperType: ['a4', 'a5', 'thermal'].includes(company.printPaperType) ? company.printPaperType : 'a4',
        thermalPaperWidth: company.thermalPaperWidth != null ? String(company.thermalPaperWidth) : '80',
        printItemNameFormat: ALLOWED_ITEM_FORMAT_IDS.includes(company.printItemNameFormat) ? company.printItemNameFormat : 'name_sku',
        showWarrantyOnInvoice: company.showWarrantyOnInvoice !== false,
        salesPrintTemplate: ALLOWED_TEMPLATE_IDS.includes(company.salesPrintTemplate) ? company.salesPrintTemplate : 'standard',
        quotationPrintTemplate: ALLOWED_TEMPLATE_IDS.includes(company.quotationPrintTemplate) ? company.quotationPrintTemplate : 'standard',
        dateFormat: company.dateFormat || 'dd/MM/yyyy',
        timezone: company.timezone || 'Asia/Dubai',
        smtpHost: company.smtpHost ?? '',
        smtpPort: company.smtpPort != null ? String(company.smtpPort) : '587',
        smtpUser: company.smtpUser ?? '',
        smtpPass: company.smtpPass ?? '',
        smtpEncryption: company.smtpEncryption || 'tls',
        mailFromAddress: company.mailFromAddress ?? '',
        mailFromName: company.mailFromName ?? '',
        whatsappProvider: company.whatsappProvider || 'none',
        whatsappInstanceId: company.whatsappInstanceId ?? '',
        whatsappToken: company.whatsappToken ?? '',
        whatsappPhoneNumberId: company.whatsappPhoneNumberId ?? '',
        whatsappAccessToken: company.whatsappAccessToken ?? '',
        barcodePrintShowCompanyName: company.barcodePrintShowCompanyName !== false,
        showWholesalePrice: company.showWholesalePrice !== false,
        enableSalesSession: company.enableSalesSession === true,
        salesSessionMode: company.salesSessionMode || 'shared',
        salesVisibilityScope: company.salesVisibilityScope || 'all',
        enableServiceModule: company.enableServiceModule === true,
        defaultCustomerId: company.defaultCustomerId ?? '',
        costPriceCodeMapping: company.costPriceCodeMapping ?? '',
        barcodeAfterScanBehavior: company.barcodeAfterScanBehavior || 'search',
        barcodeAfterScanBehaviorSales: company.barcodeAfterScanBehaviorSales || company.barcodeAfterScanBehavior || 'search',
        barcodeAfterScanBehaviorPurchase: company.barcodeAfterScanBehaviorPurchase || 'quantity',
        duplicateItemBehaviorSales: company.duplicateItemBehaviorSales || (company.barcodeAfterScanBehaviorSales === 'auto_increment' ? 'auto_increment' : (company.barcodeAfterScanBehaviorSales === 'add_new' ? 'add_new' : 'dialog')),
        duplicateItemBehaviorPurchase: company.duplicateItemBehaviorPurchase || (company.barcodeAfterScanBehaviorPurchase === 'auto_increment' ? 'auto_increment' : (company.barcodeAfterScanBehaviorPurchase === 'add_new' ? 'add_new' : 'dialog')),
        enableNetworkThermalPrinter: company.enableNetworkThermalPrinter === true,
        networkPrinterIp: company.networkPrinterIp ?? '',
        networkPrinterPort: company.networkPrinterPort != null ? String(company.networkPrinterPort) : '9100',
        enableDirectPrintSilently: company.enableDirectPrintSilently === true,
        enableNegativeStockSales: company.enableNegativeStockSales === true,
        stayOnSalesFormAfterSave: company.stayOnSalesFormAfterSave === true,
        stockValuationMethod: company.stockValuationMethod || 'LPC',
        enableAutoPrintOnSave: company.enableAutoPrintOnSave === true,
        enableQzPrint: company.enableQzPrint === true,
        qzPrinterName: company.qzPrinterName ?? '',
        qzBarcodePrinterName: company.qzBarcodePrinterName ?? '',
        enableQuickScanPrice: company.enableQuickScanPrice !== false,
      });
        setSystemSettings((prev) => ({
          ...prev,
          lowStockThreshold:
            company.lowStockThreshold != null && company.lowStockThreshold !== ''
              ? Number(company.lowStockThreshold)
              : 10,
          stockValuationMethod: company.stockValuationMethod || 'LPC',
        }));
      }
    }, [company]);
  
    const [systemSettings, setSystemSettings] = useState({
      enableNotifications: true,
      enableEmailAlerts: true,
      lowStockThreshold: 10,
      stockValuationMethod: 'LPC',
    });
  
    const canUpdateSystemSettings = isAdmin || userHasPerm('settings.settings.update');
  
    const handleSaveSystemSettings = async () => {
      setSystemSaveSuccess(false);
      try {
        await updateCompany({
          lowStockThreshold:
            Number.isFinite(Number(systemSettings.lowStockThreshold)) && Number(systemSettings.lowStockThreshold) >= 0
              ? Number(systemSettings.lowStockThreshold)
              : 10,
          stockValuationMethod: systemSettings.stockValuationMethod,
        }).unwrap();
        setSystemSaveSuccess(true);
      } catch (err) {
        console.error(err);
        alert(err?.data?.message || 'Failed to save system settings');
      }
    };
  
    const handleConfigChange = (field, value) => {
      setConfig((prev) => ({ ...prev, [field]: value }));
      setSaveSuccess(false);
    };

    const handleFindQzPrinters = async () => {
      setSearchingQzPrinters(true);
      try {
        const printers = await qzService.getLocalPrinters();
        setQzPrintersList(printers);
        if (printers.length > 0 && !config.qzPrinterName) {
          setConfig(prev => ({ ...prev, qzPrinterName: printers[0] }));
        }
      } catch (err) {
        alert(err.message || 'Failed to search for local printers. Please check if QZ Tray is running.');
      } finally {
        setSearchingQzPrinters(false);
      }
    };
  
    const handleSaveConfiguration = async () => {
      setSaveSuccess(false);
      try {
        await updateCompany({
          name: config.name.trim() || undefined,
          nameArabic: config.nameArabic?.trim() || undefined,
          website: config.website?.trim() || undefined,
          email: config.email.trim() || undefined,
          phone: config.phone.trim() || undefined,
          address: config.address.trim() || undefined,
          currency: config.currency,
          currencyDecimals: Number(config.currencyDecimals),
          taxId: config.taxId.trim() || undefined,
          vatRate: config.vatRate === '' ? undefined : parseFloat(config.vatRate),
          vatNumber: config.vatNumber.trim() || undefined,
          taxLabel: config.taxLabel || 'VAT',
          salesVatIncluded: config.salesVatIncluded,
          purchaseVatIncluded: config.purchaseVatIncluded,
          creditDaysOptions: config.creditDaysOptions?.trim() || undefined,
          maxDiscountType:
            config.maxDiscountType === 'percent' || config.maxDiscountType === 'amount'
              ? config.maxDiscountType
              : null,
          maxDiscountPercent:
            config.maxDiscountType === 'percent' && config.maxDiscountPercent !== ''
              ? (() => {
                  const n = parseFloat(config.maxDiscountPercent);
                  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
                })()
              : null,
          maxDiscountAmount:
            config.maxDiscountType === 'amount' && config.maxDiscountAmount !== ''
              ? (() => {
                  const n = parseFloat(config.maxDiscountAmount);
                  return Number.isFinite(n) && n >= 0 ? n : null;
                })()
              : null,
          invoicePrefix: config.invoicePrefix?.trim() || 'INV',
          invoiceNextNumber: config.invoiceNextNumber === '' ? 1 : parseInt(config.invoiceNextNumber, 10) || 1,
          invoiceNumberPadding: config.invoiceNumberPadding === '' ? 6 : parseInt(config.invoiceNumberPadding, 10) || 6,
          printPaperType: ['a4', 'a5', 'thermal'].includes(config.printPaperType) ? config.printPaperType : 'a4',
          thermalPaperWidth: config.thermalPaperWidth === '' ? 80 : parseInt(config.thermalPaperWidth, 10) || 80,
          printItemNameFormat: ALLOWED_ITEM_FORMAT_IDS.includes(config.printItemNameFormat)
            ? config.printItemNameFormat
            : 'name_sku',
          showWarrantyOnInvoice: config.showWarrantyOnInvoice,
          salesPrintTemplate: ALLOWED_TEMPLATE_IDS.includes(config.salesPrintTemplate)
            ? config.salesPrintTemplate
            : 'standard',
          quotationPrintTemplate: ALLOWED_TEMPLATE_IDS.includes(config.quotationPrintTemplate)
            ? config.quotationPrintTemplate
            : 'standard',
          dateFormat:
            ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd', 'dd-MM-yyyy'].includes(config.dateFormat)
              ? config.dateFormat
              : 'dd/MM/yyyy',
          timezone: config.timezone || 'Asia/Dubai',
          smtpHost: config.smtpHost || null,
          smtpPort: config.smtpPort ? parseInt(config.smtpPort, 10) : null,
          smtpUser: config.smtpUser || null,
          smtpPass: config.smtpPass || null,
          smtpEncryption: config.smtpEncryption || 'tls',
          mailFromAddress: config.mailFromAddress || null,
          mailFromName: config.mailFromName || null,
          whatsappProvider: config.whatsappProvider,
          whatsappInstanceId: config.whatsappInstanceId?.trim() || null,
          whatsappToken: config.whatsappToken?.trim() || null,
          whatsappPhoneNumberId: config.whatsappPhoneNumberId?.trim() || null,
          whatsappAccessToken: config.whatsappAccessToken?.trim() || null,
          barcodePrintShowCompanyName: config.barcodePrintShowCompanyName,
          showWholesalePrice: config.showWholesalePrice,
          enableSalesSession: config.enableSalesSession,
          salesSessionMode: config.salesSessionMode,
          salesVisibilityScope: config.salesVisibilityScope,
          enableServiceModule: config.enableServiceModule,
          defaultCustomerId: config.defaultCustomerId || null,
          costPriceCodeMapping: config.costPriceCodeMapping?.trim() || null,
          barcodeAfterScanBehavior: config.barcodeAfterScanBehaviorSales || config.barcodeAfterScanBehavior,
          barcodeAfterScanBehaviorSales: config.barcodeAfterScanBehaviorSales,
          barcodeAfterScanBehaviorPurchase: config.barcodeAfterScanBehaviorPurchase,
          duplicateItemBehaviorSales: config.duplicateItemBehaviorSales || 'dialog',
          duplicateItemBehaviorPurchase: config.duplicateItemBehaviorPurchase || 'dialog',
          enableNetworkThermalPrinter: config.enableNetworkThermalPrinter,
          networkPrinterIp: config.networkPrinterIp?.trim() || null,
          networkPrinterPort: config.networkPrinterPort ? parseInt(config.networkPrinterPort, 10) : 9100,
          enableDirectPrintSilently: config.enableDirectPrintSilently,
          enableNegativeStockSales: config.enableNegativeStockSales,
          stayOnSalesFormAfterSave: config.stayOnSalesFormAfterSave,
          enableAutoPrintOnSave: config.enableAutoPrintOnSave,
          enableQzPrint: config.enableQzPrint,
          qzPrinterName: config.qzPrinterName?.trim() || null,
          qzBarcodePrinterName: config.qzBarcodePrinterName?.trim() || null,
        }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      console.error(err);
    }
  };

  const openTemplatePreview = (templateId, doc) => {
    navigate(`/settings/print-templates/preview/${templateId}?doc=${doc}`);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Settings
      </Typography>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab icon={<BusinessIcon />} label="Configuration" iconPosition="start" />
        <Tab icon={<PrintIcon />} label="Printer" iconPosition="start" />
        <Tab icon={<PaletteIcon />} label="Invoice Design Studio" iconPosition="start" />
        <Tab icon={<EmailIcon />} label="Email" iconPosition="start" />
        <Tab icon={<WhatsAppIcon />} label="WhatsApp" iconPosition="start" />
        <Tab icon={<SettingsIcon />} label="System" iconPosition="start" />
        <Tab icon={<SecurityIcon />} label="Permissions" iconPosition="start" />
        <Tab icon={<ReorderIcon />} label="Menu & Navigation" iconPosition="start" />
      </Tabs>

      {tabValue === 2 && (
        <DocumentDesignCustomizer company={company} />
      )}

      {tabValue === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Email Configuration (SMTP)
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="SMTP Host"
                  value={config.smtpHost}
                  onChange={(e) => handleConfigChange('smtpHost', e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="SMTP Port"
                  value={config.smtpPort}
                  onChange={(e) => handleConfigChange('smtpPort', e.target.value)}
                  placeholder="587"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="SMTP Username"
                  value={config.smtpUser}
                  onChange={(e) => handleConfigChange('smtpUser', e.target.value)}
                  placeholder="yourname@gmail.com"
                  helperText="Typically your full email address."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="SMTP Password"
                  type="password"
                  value={config.smtpPass}
                  onChange={(e) => handleConfigChange('smtpPass', e.target.value)}
                  helperText="Your email password or App Password if using Gmail/Outlook."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Encryption"
                  value={config.smtpEncryption}
                  onChange={(e) => handleConfigChange('smtpEncryption', e.target.value)}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="ssl">SSL (465)</MenuItem>
                  <MenuItem value="tls">TLS (587)</MenuItem>
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Sender Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="From Email Address"
                      value={config.mailFromAddress}
                      onChange={(e) => handleConfigChange('mailFromAddress', e.target.value)}
                      placeholder="noreply@yourcompany.com"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="From Name"
                      value={config.mailFromName}
                      onChange={(e) => handleConfigChange('mailFromName', e.target.value)}
                      placeholder="My Company Sales"
                    />
                  </Grid>
                </Grid>
              </Grid>
              {saveSuccess && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">Email settings saved successfully.</Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveConfiguration}>
                  Save Email Settings
                </Button>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Email Templates
                    </Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} size="small" onClick={() => navigate('/settings/email-templates/new')}>
                      Create Template
                    </Button>
                  </Box>
                  <EmailTemplatesList />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 4 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              WhatsApp Configuration
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Connect your account to <strong>UltraMsg</strong> for automatic WhatsApp messaging. If active, invoices will be sent directly as PDF documents to your customers.
                </Alert>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Service Provider"
                  value={config.whatsappProvider}
                  onChange={(e) => handleConfigChange('whatsappProvider', e.target.value)}
                  helperText={config.whatsappProvider === 'none' ? 'No API. Uses wa.me fallback link.' : 'Automatic delivery active.'}
                >
                  <MenuItem value="none">None (Share Link Fallback)</MenuItem>
                  <MenuItem value="meta">Meta (Official API)</MenuItem>
                  <MenuItem value="ultraMsg">UltraMsg (Cloud API)</MenuItem>
                </TextField>
              </Grid>
              
              {config.whatsappProvider === 'meta' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number ID"
                      value={config.whatsappPhoneNumberId}
                      onChange={(e) => handleConfigChange('whatsappPhoneNumberId', e.target.value)}
                      placeholder="e.g. 1029384756"
                      helperText="From Meta App Dashboard → WhatsApp → Configuration."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Permanent Access Token"
                      value={config.whatsappAccessToken}
                      onChange={(e) => handleConfigChange('whatsappAccessToken', e.target.value)}
                      placeholder="EAAG...."
                      type="password"
                      multiline
                      rows={2}
                      helperText="Generated from User Assets in Meta Business Settings."
                    />
                  </Grid>
                </>
              )}

              {config.whatsappProvider === 'ultraMsg' && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Instance ID"
                      value={config.whatsappInstanceId}
                      onChange={(e) => handleConfigChange('whatsappInstanceId', e.target.value)}
                      placeholder="e.g. instance12345"
                      helperText="Your UltraMsg instance ID."
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Token"
                      value={config.whatsappToken}
                      onChange={(e) => handleConfigChange('whatsappToken', e.target.value)}
                      placeholder="Your UltraMsg API token"
                      type="password"
                      helperText="Your UltraMsg API token."
                    />
                  </Grid>
                </>
              )}

              {saveSuccess && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">WhatsApp settings saved successfully.</Alert>
                </Grid>
              )}
              
              <Grid size={{ xs: 12 }}>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveConfiguration} sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}>
                  Save WhatsApp Settings
                </Button>
              </Grid>
              
              <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account? Visit <a href="https://ultramsg.com" target="_blank" rel="noopener noreferrer" style={{ color: '#128C7E', fontWeight: 600 }}>UltraMsg</a> to get your Instance ID and Token.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Company Configuration
            </Typography>
            {loadingCompany ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                    Company Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        bgcolor: 'grey.50',
                      }}
                    >
                      {company?.logoUrl ? (
                        <img
                          src={company.logoUrl}
                          alt="Company logo"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      )}
                    </Box>
                    <Box>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const formData = new FormData();
                            formData.append('logo', file);
                            await updateCompanyLogo(formData).unwrap();
                          } catch (err) {
                            alert(err?.data?.message || 'Failed to upload logo');
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        startIcon={<ImageIcon />}
                      >
                        {uploadingLogo ? 'Uploading...' : company?.logoUrl ? 'Change logo' : 'Set logo'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Main Company
                  </Typography>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={config.name}
                    onChange={(e) => handleConfigChange('name', e.target.value)}
                    placeholder="Your company legal name"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Company Name (Arabic)"
                    value={config.nameArabic}
                    onChange={(e) => handleConfigChange('nameArabic', e.target.value)}
                    placeholder="اسم الشركة"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={config.website}
                    onChange={(e) => handleConfigChange('website', e.target.value)}
                    placeholder="https://www.example.com"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={config.email}
                    onChange={(e) => handleConfigChange('email', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={config.phone}
                    onChange={(e) => handleConfigChange('phone', e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Company Address
                  </Typography>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={3}
                    value={config.address}
                    onChange={(e) => handleConfigChange('address', e.target.value)}
                    placeholder="Street, City, State, Country, Postal Code"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Currency
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Currency"
                    value={config.currency}
                    onChange={(e) => handleConfigChange('currency', e.target.value)}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Decimal Precision
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Decimal Places"
                    value={config.currencyDecimals}
                    onChange={(e) => handleConfigChange('currencyDecimals', e.target.value)}
                  >
                    {[0, 1, 2, 3, 4].map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }} display="flex" alignItems="center" gap={0.5}>
                    <ReceiptLongIcon fontSize="small" /> Tax Configuration (e.g. VAT / No Tax)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        select
                        label="Tax label"
                        value={config.taxLabel}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleConfigChange('taxLabel', val);
                          if (val === 'None') {
                            handleConfigChange('vatRate', '0');
                          }
                        }}
                      >
                        {TAX_LABELS.map((l) => (
                          <MenuItem key={l} value={l}>
                            {l === 'None' ? 'None (No Tax / 0%)' : l}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="VAT / Tax rate (%)"
                        value={config.taxLabel === 'None' ? '0' : config.vatRate}
                        onChange={(e) => handleConfigChange('vatRate', e.target.value)}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        placeholder="e.g. 15"
                        disabled={config.taxLabel === 'None'}
                        helperText={config.taxLabel === 'None' ? 'Tax is disabled for this company' : ''}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="VAT / Tax registration number"
                        value={config.vatNumber}
                        onChange={(e) => handleConfigChange('vatNumber', e.target.value)}
                        placeholder="e.g. VAT number"
                        disabled={config.taxLabel === 'None'}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        label="Tax ID (optional)"
                        value={config.taxId}
                        onChange={(e) => handleConfigChange('taxId', e.target.value)}
                        placeholder="Business tax identifier"
                        disabled={config.taxLabel === 'None'}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Credit sales – payment terms (days)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Credit days options"
                    value={config.creditDaysOptions}
                    onChange={(e) => handleConfigChange('creditDaysOptions', e.target.value)}
                    placeholder="e.g. 7, 14, 30"
                    helperText="Comma-separated days (e.g. 7, 14, 30). Shown when creating credit sales."
                    sx={{ maxWidth: 400 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Date format
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Date format"
                    value={config.dateFormat}
                    onChange={(e) => handleConfigChange('dateFormat', e.target.value)}
                    sx={{ maxWidth: 300 }}
                    helperText="Controls how dates are shown across the system."
                  >
                    <MenuItem value="dd/MM/yyyy">dd/MM/yyyy (e.g. 31/12/2026)</MenuItem>
                    <MenuItem value="MM/dd/yyyy">MM/dd/yyyy (e.g. 12/31/2026)</MenuItem>
                    <MenuItem value="yyyy-MM-dd">yyyy-MM-dd (e.g. 2026-12-31)</MenuItem>
                    <MenuItem value="dd-MM-yyyy">dd-MM-yyyy (e.g. 31-12-2026)</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Company Timezone
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    label="Timezone"
                    value={config.timezone || 'Asia/Dubai'}
                    onChange={(e) => handleConfigChange('timezone', e.target.value)}
                    sx={{ maxWidth: 450 }}
                    helperText="Controls report date boundaries and daily cut-offs for this company."
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <MenuItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Maximum discount (sales)
                  </Typography>
                  <RadioGroup
                    row
                    value={config.maxDiscountType || 'none'}
                    onChange={(e) => handleConfigChange('maxDiscountType', e.target.value === 'none' ? '' : e.target.value)}
                  >
                    <FormControlLabel value="none" control={<Radio />} label="No limit" />
                    <FormControlLabel value="percent" control={<Radio />} label="Percentage" />
                    <FormControlLabel value="amount" control={<Radio />} label="Amount per line" />
                  </RadioGroup>
                  {config.maxDiscountType === 'percent' && (
                    <TextField
                      fullWidth
                      type="number"
                      label="Maximum discount %"
                      value={config.maxDiscountPercent}
                      onChange={(e) => handleConfigChange('maxDiscountPercent', e.target.value)}
                      placeholder="e.g. 20"
                      helperText="Maximum discount % allowed per line on sales."
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      sx={{ maxWidth: 400, mt: 1 }}
                    />
                  )}
                  {config.maxDiscountType === 'amount' && (
                    <TextField
                      fullWidth
                      type="number"
                      label={`Maximum discount amount (${company?.currency || 'USD'})`}
                      value={config.maxDiscountAmount}
                      onChange={(e) => handleConfigChange('maxDiscountAmount', e.target.value)}
                      placeholder="e.g. 10"
                      helperText="Maximum discount amount allowed per line on sales."
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={{ maxWidth: 400, mt: 1 }}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Invoice number sequence (Sales)
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        label="Invoice prefix"
                        value={config.invoicePrefix}
                        onChange={(e) => handleConfigChange('invoicePrefix', e.target.value)}
                        placeholder="e.g. INV"
                        helperText="Used when Invoice Number is left empty."
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Next invoice number"
                        value={config.invoiceNextNumber}
                        onChange={(e) => handleConfigChange('invoiceNextNumber', e.target.value)}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Next sequence value to use."
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Zero padding"
                        value={config.invoiceNumberPadding}
                        onChange={(e) => handleConfigChange('invoiceNumberPadding', e.target.value)}
                        inputProps={{ min: 0, max: 12, step: 1 }}
                        helperText="e.g. 6 => 000001"
                        sx={{ maxWidth: 300 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Default Invoice Customer
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => `${option.name} ${option.phone ? `(${option.phone})` : ''}`}
                        value={customers.find((c) => c.id === config.defaultCustomerId) || null}
                        onChange={(event, newValue) => {
                          handleConfigChange('defaultCustomerId', newValue?.id || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Default Customer Selection"
                            helperText="Pre-selected customer when creating a new invoice/sales order."
                          />
                        )}
                        sx={{ maxWidth: 450 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Sales &amp; Purchase VAT
                  </Typography>
                  <Grid container spacing={3} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Sales prices
                      </Typography>
                      <RadioGroup
                        row
                        value={config.salesVatIncluded ? 'included' : 'excluded'}
                        onChange={(e) => handleConfigChange('salesVatIncluded', e.target.value === 'included')}
                      >
                        <FormControlLabel value="included" control={<Radio />} label="VAT included" />
                        <FormControlLabel value="excluded" control={<Radio />} label="VAT excluded" />
                      </RadioGroup>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Whether selling prices already include {config.taxLabel || 'VAT'} or not
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" fontWeight="medium" gutterBottom>
                        Purchase prices
                      </Typography>
                      <RadioGroup
                        row
                        value={config.purchaseVatIncluded ? 'included' : 'excluded'}
                        onChange={(e) => handleConfigChange('purchaseVatIncluded', e.target.value === 'included')}
                      >
                        <FormControlLabel value="included" control={<Radio />} label="VAT included" />
                        <FormControlLabel value="excluded" control={<Radio />} label="VAT excluded" />
                      </RadioGroup>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Whether purchase prices already include {config.taxLabel || 'VAT'} or not
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Product settings
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.showWholesalePrice}
                        onChange={(e) => handleConfigChange('showWholesalePrice', e.target.checked)}
                      />
                    }
                    label="Enable Wholesale Price & Features (Product, POS/Sales)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Enable or disable wholesale pricing. When disabled, wholesale pricing and wholesale buttons are hidden in POS/Sales screens and product forms.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enableServiceModule}
                        onChange={(e) => handleConfigChange('enableServiceModule', e.target.checked)}
                      />
                    }
                    label="Enable Mobile Service / Repair Invoicing Module"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Enables repair diagnostic tracking, service billing bypass for inventory checks, and specialized service invoice print templates.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enableNegativeStockSales}
                        onChange={(e) => handleConfigChange('enableNegativeStockSales', e.target.checked)}
                      />
                    }
                    label="Enable Negative Stock Sales"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Allow all users to make sales even when inventory levels are insufficient.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.stayOnSalesFormAfterSave}
                        onChange={(e) => handleConfigChange('stayOnSalesFormAfterSave', e.target.checked)}
                      />
                    }
                    label="Stay on New Sales Form after Saving"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    When creating a sale, instead of redirecting to the list page upon saving, clear the form and stay on the new sale screen for the next transaction.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Cost Price Code Mapping (Barcode)"
                    value={config.costPriceCodeMapping}
                    onChange={(e) => handleConfigChange('costPriceCodeMapping', e.target.value.toUpperCase().slice(0, 10))}
                    placeholder="e.g. BLACKHORSE"
                    helperText="10 letters mapping digits 0-9 for barcode labels. E.g. BLACKHORSE -> B=0, L=1, A=2..."
                    sx={{ maxWidth: 400, mt: 2 }}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', mt: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QrCodeScannerIcon fontSize="small" color="primary" /> Barcode Scanner Behavior
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                      Customize how the system reacts when a product barcode is scanned in Sales vs. Purchase screens.
                    </Typography>

                    <Grid container spacing={3}>
                      {/* Sales Section */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Sales & POS Scanner Settings
                        </Typography>
                        <Stack spacing={2.5}>
                          <TextField
                            select
                            fullWidth
                            label="Sales Scanner Focus Behavior"
                            value={config.barcodeAfterScanBehaviorSales || 'search'}
                            onChange={(e) => handleConfigChange('barcodeAfterScanBehaviorSales', e.target.value)}
                            helperText="Target field to focus immediately after scanning a product barcode in Sales/POS"
                          >
                            <MenuItem value="search">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Search Bar (Continuous Scanning)</Typography>
                                <Typography variant="caption" color="text.secondary">Ready to scan the next product immediately (recommended for high-speed POS)</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="quantity">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Quantity Field</Typography>
                                <Typography variant="caption" color="text.secondary">Automatically highlights quantity field to enter amount sold</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="price">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Selling Price Field</Typography>
                                <Typography variant="caption" color="text.secondary">Automatically jumps to price field to adjust selling price</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="auto_increment">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Increment Quantity (+1)</Typography>
                                <Typography variant="caption" color="text.secondary">Scanning increments quantity by +1</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="add_new">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Always Add as New Row</Typography>
                                <Typography variant="caption" color="text.secondary">Scanning adds a new line item directly</Typography>
                              </Box>
                            </MenuItem>
                          </TextField>

                          <TextField
                            select
                            fullWidth
                            label="Sales Duplicate Item Handling"
                            value={config.duplicateItemBehaviorSales || 'dialog'}
                            onChange={(e) => handleConfigChange('duplicateItemBehaviorSales', e.target.value)}
                            helperText="Action when a product already present in the sales order is scanned or selected"
                          >
                            <MenuItem value="dialog">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Show Confirmation Dialog</Typography>
                                <Typography variant="caption" color="text.secondary">Prompt cashier to choose: Increase Qty, Add as New Row, or Cancel</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="auto_increment">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Increment Existing Item (+1)</Typography>
                                <Typography variant="caption" color="text.secondary">Directly increment quantity of existing item without confirmation dialog</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="add_new">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Always Add as New Row (Separate Line)</Typography>
                                <Typography variant="caption" color="text.secondary">Directly insert a new line item without confirmation dialog</Typography>
                              </Box>
                            </MenuItem>
                          </TextField>
                        </Stack>
                      </Grid>

                      {/* Purchase Section */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Purchase & Receiving Scanner Settings
                        </Typography>
                        <Stack spacing={2.5}>
                          <TextField
                            select
                            fullWidth
                            label="Purchase Scanner Focus Behavior"
                            value={config.barcodeAfterScanBehaviorPurchase || 'quantity'}
                            onChange={(e) => handleConfigChange('barcodeAfterScanBehaviorPurchase', e.target.value)}
                            helperText="Target field to focus immediately after scanning a product in Purchase Orders/Receiving"
                          >
                            <MenuItem value="quantity">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Quantity Field</Typography>
                                <Typography variant="caption" color="text.secondary">Immediately highlights quantity field to enter received bulk quantity</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="cost">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Unit Cost Field</Typography>
                                <Typography variant="caption" color="text.secondary">Jumps to unit cost field to enter or verify supplier invoice price</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="search">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Focus Search Bar (Continuous Scanning)</Typography>
                                <Typography variant="caption" color="text.secondary">Ready to scan next product SKU/carton immediately</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="auto_increment">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Increment Quantity (+1)</Typography>
                                <Typography variant="caption" color="text.secondary">Scanning increments purchase quantity by 1</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="add_new">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Always Add as New Row</Typography>
                                <Typography variant="caption" color="text.secondary">Scanning adds a new line item directly</Typography>
                              </Box>
                            </MenuItem>
                          </TextField>

                          <TextField
                            select
                            fullWidth
                            label="Purchase Duplicate Item Handling"
                            value={config.duplicateItemBehaviorPurchase || 'dialog'}
                            onChange={(e) => handleConfigChange('duplicateItemBehaviorPurchase', e.target.value)}
                            helperText="Action when a product already present in the purchase order is scanned or selected"
                          >
                            <MenuItem value="dialog">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Show Confirmation Dialog</Typography>
                                <Typography variant="caption" color="text.secondary">Prompt user to choose: Increase Qty, Add as New Row, or Cancel</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="auto_increment">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Auto-Increment Existing Item (+1)</Typography>
                                <Typography variant="caption" color="text.secondary">Directly increment quantity of existing item without confirmation dialog</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="add_new">
                              <Box sx={{ py: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Always Add as New Row (Separate Line)</Typography>
                                <Typography variant="caption" color="text.secondary">Directly insert a new line item without confirmation dialog</Typography>
                              </Box>
                            </MenuItem>
                          </TextField>
                        </Stack>
                      </Grid>

                      {/* Enable Quick Price Override in POS Scanner */}
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enableQuickScanPrice !== false}
                              onChange={(e) => handleConfigChange('enableQuickScanPrice', e.target.checked)}
                            />
                          }
                          label="Enable Quick Price Override Box in POS Scanner"
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          When enabled, displays a price input box in the barcode scanner bar. Cashiers can enter a custom price before scanning a product to automatically override its selling price.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Sales / Register Shift Sessions & Visibility Settings
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enableSalesSession}
                        onChange={(e) => handleConfigChange('enableSalesSession', e.target.checked)}
                      />
                    }
                    label="Enable Register Shift Sessions (Opening/Closing)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Require cashiers to enter an opening balance before creating sales and perform shift reconciliation upon closing.
                  </Typography>

                  {config.enableSalesSession && (
                    <Box sx={{ mt: 1.5, mb: 2.5, pl: 1 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Shift Session Mode (Multi-User Behavior)"
                        value={config.salesSessionMode || 'shared'}
                        onChange={(e) => handleConfigChange('salesSessionMode', e.target.value)}
                        helperText={
                          (config.salesSessionMode || 'shared') === 'shared'
                            ? 'Shared Shift: All cashiers share one active shift and cash drawer. When one user opens a shift, any other user who logs in continues using that same open shift.'
                            : 'Individual Shift: Each cashier must open and close their own separate register session and drawer independently.'
                        }
                      >
                        <MenuItem value="shared">
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Shared Store Shift (One Shift for Entire Store / Counter)</Typography>
                            <Typography variant="caption" color="text.secondary">All staff share the active shift and cash drawer</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="user">
                          <Box sx={{ py: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>Individual Cashier Shift (Per-User Shift)</Typography>
                            <Typography variant="caption" color="text.secondary">Each user opens, manages, and closes their own separate shift</Typography>
                          </Box>
                        </MenuItem>
                      </TextField>
                    </Box>
                  )}

                  <Box sx={{ mt: 2, pl: 1 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Sales Orders List Visibility (Non-Admin Users)"
                      value={config.salesVisibilityScope || 'all'}
                      onChange={(e) => handleConfigChange('salesVisibilityScope', e.target.value)}
                      helperText={
                        (config.salesVisibilityScope || 'all') === 'all'
                          ? 'All Users: Non-admin users can view and access sales orders created by everyone in the company.'
                          : 'Logged-in User Only: Non-admin users can only view and manage sales orders created by themselves.'
                      }
                    >
                      <MenuItem value="all">
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Show All Sales (Company-Wide)</Typography>
                          <Typography variant="caption" color="text.secondary">Non-admin staff can view all sales orders</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="user_only">
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Show Logged-in User Sales Only</Typography>
                          <Typography variant="caption" color="text.secondary">Non-admin staff can only see their own sales</Typography>
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Box>
                </Grid>
                {saveSuccess && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="success" onClose={() => setSaveSuccess(false)}>
                      Configuration saved successfully.
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSaveConfiguration}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Printer Settings
            </Typography>
            {loadingCompany ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Printer configuration
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        select
                        label="Print format"
                        value={config.printPaperType}
                        onChange={(e) => handleConfigChange('printPaperType', e.target.value)}
                        sx={{ maxWidth: 300 }}
                      >
                        <MenuItem value="a4">A4</MenuItem>
                        <MenuItem value="a5">A5</MenuItem>
                        <MenuItem value="thermal">Thermal</MenuItem>
                      </TextField>
                    </Grid>
                    {config.printPaperType === 'thermal' && (
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          select
                          label="Thermal width"
                          value={config.thermalPaperWidth}
                          onChange={(e) => handleConfigChange('thermalPaperWidth', e.target.value)}
                          helperText="Common sizes: 58mm or 80mm"
                          sx={{ maxWidth: 300 }}
                        >
                          <MenuItem value="58">58mm</MenuItem>
                          <MenuItem value="80">80mm</MenuItem>
                        </TextField>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, md: 4 }} display="flex" alignItems="center">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.enableAutoPrintOnSave}
                            onChange={(e) => handleConfigChange('enableAutoPrintOnSave', e.target.checked)}
                          />
                        }
                        label="Auto Print on Save & Print"
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Direct Network Printing (Thermal)
                    </Typography>
                    <Grid container spacing={3} alignItems="center">
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enableNetworkThermalPrinter}
                              onChange={(e) => handleConfigChange('enableNetworkThermalPrinter', e.target.checked)}
                            />
                          }
                          label="Enable Direct Network Printing"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enableDirectPrintSilently}
                              onChange={(e) => handleConfigChange('enableDirectPrintSilently', e.target.checked)}
                              disabled={!config.enableNetworkThermalPrinter}
                            />
                          }
                          label="Print Silently (Automatic)"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Printer IP Address"
                          value={config.networkPrinterIp || ''}
                          onChange={(e) => handleConfigChange('networkPrinterIp', e.target.value)}
                          disabled={!config.enableNetworkThermalPrinter}
                          placeholder="e.g. 192.168.1.100"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="Printer Port"
                          value={config.networkPrinterPort || '9100'}
                          onChange={(e) => handleConfigChange('networkPrinterPort', e.target.value)}
                          disabled={!config.enableNetworkThermalPrinter}
                          placeholder="9100"
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Button
                          variant="outlined"
                          onClick={async () => {
                            try {
                              await testPrinter({ 
                                ip: config.networkPrinterIp, 
                                port: config.networkPrinterPort 
                              }).unwrap();
                              alert('Printer connection successful!');
                            } catch (err) {
                              alert(`Connection failed: ${err.data?.message || err.message}`);
                            }
                          }}
                          disabled={!config.enableNetworkThermalPrinter || testingPrinter || !config.networkPrinterIp}
                          sx={{ mt: 1 }}
                        >
                          {testingPrinter ? 'Testing...' : 'Test Connection'}
                        </Button>
                      </Grid>
                    </Grid>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      When enabled, the system will attempt to send thermal receipts directly to this IP address over your local network, bypassing the browser's print dialog.
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Local Printing via QZ Tray
                    </Typography>
                    <Grid container spacing={3} alignItems="center">
                      <Grid size={{ xs: 12, md: 4 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={config.enableQzPrint}
                              onChange={(e) => handleConfigChange('enableQzPrint', e.target.checked)}
                            />
                          }
                          label="Enable QZ Tray Printing"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="QZ Sales/Invoice Printer Name"
                          value={config.qzPrinterName || ''}
                          onChange={(e) => handleConfigChange('qzPrinterName', e.target.value)}
                          disabled={!config.enableQzPrint}
                          placeholder="e.g. Receipt Printer"
                          helperText="Exact printer name on local computer"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth
                          label="QZ Barcode Printer Name"
                          value={config.qzBarcodePrinterName || ''}
                          onChange={(e) => handleConfigChange('qzBarcodePrinterName', e.target.value)}
                          disabled={!config.enableQzPrint}
                          placeholder="e.g. Zebra Label Printer"
                          helperText="Printer used for barcode labels"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Button
                          variant="outlined"
                          onClick={handleFindQzPrinters}
                          disabled={!config.enableQzPrint || searchingQzPrinters}
                          sx={{ mt: 1 }}
                        >
                          {searchingQzPrinters ? 'Searching...' : 'Find Local Printers'}
                        </Button>
                      </Grid>
                      {qzPrintersList.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                            Detected Local Printers:
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {qzPrintersList.map((printer) => (
                              <Chip
                                key={printer}
                                label={printer}
                                onClick={() => {
                                  // Auto-fill whichever is empty, prioritize sales
                                  if (!config.qzPrinterName) {
                                    handleConfigChange('qzPrinterName', printer);
                                  } else {
                                    handleConfigChange('qzBarcodePrinterName', printer);
                                  }
                                }}
                                color={(config.qzPrinterName === printer || config.qzBarcodePrinterName === printer) ? 'primary' : 'default'}
                                variant={(config.qzPrinterName === printer || config.qzBarcodePrinterName === printer) ? 'filled' : 'outlined'}
                                size="small"
                                sx={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      When enabled, the system connects to the local QZ Tray app to send high-fidelity PDF prints directly to your printer without displaying browser print dialogs.
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Sales print item format
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        select
                        label="Item name format"
                        value={config.printItemNameFormat}
                        onChange={(e) => handleConfigChange('printItemNameFormat', e.target.value)}
                        sx={{ maxWidth: 420 }}
                      >
                        {ITEM_NAME_FORMAT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.showWarrantyOnInvoice}
                            onChange={(e) => handleConfigChange('showWarrantyOnInvoice', e.target.checked)}
                          />
                        }
                        label="Show Warranty & Serial Numbers on Invoice Print"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                    Barcode print settings
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.barcodePrintShowCompanyName}
                        onChange={(e) => handleConfigChange('barcodePrintShowCompanyName', e.target.checked)}
                      />
                    }
                    label="Include Company Name on Barcode Labels"
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Templates
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Sales invoice templates (click to choose)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {SALES_TEMPLATES.map((tpl) => {
                          const selected = config.salesPrintTemplate === tpl.id;
                          const isThermal = tpl.id === 'thermal';
                          return (
                            <Card
                              key={tpl.id}
                              variant={selected ? 'outlined' : 'elevation'}
                              onClick={() => handleConfigChange('salesPrintTemplate', tpl.id)}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                boxShadow: selected ? 4 : 1,
                                minWidth: 220,
                                maxWidth: 260,
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: selected ? 700 : 500, mb: 0.5 }}
                                  color={selected ? 'primary.main' : 'text.primary'}
                                >
                                  {tpl.label}
                                </Typography>

                                {/* Mini visual preview */}
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    height: 70,
                                    display: 'flex',
                                    flexDirection: isThermal ? 'column' : 'column',
                                    alignItems: 'stretch',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: 10,
                                      bgcolor: 'grey.300',
                                      borderRadius: 0.5,
                                      mb: 0.5,
                                      width: isThermal ? '70%' : '50%',
                                      mx: 'auto',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: isThermal ? 'column' : 'row',
                                      gap: 0.4,
                                      flex: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        flex: isThermal ? 'none' : 1,
                                        height: isThermal ? 8 : '100%',
                                        bgcolor: 'grey.200',
                                        borderRadius: 0.5,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        flex: 1,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.3,
                                      }}
                                    >
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                    </Box>
                                  </Box>
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                  {tpl.description}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openTemplatePreview(tpl.id, 'sales');
                                    }}
                                  >
                                    Preview
                                  </Button>
                                </Box>
                                {selected && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                      label="Selected"
                                    />
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                        Quotation templates (click to choose)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {SALES_TEMPLATES.map((tpl) => {
                          const selected = config.quotationPrintTemplate === tpl.id;
                          const isThermal = tpl.id === 'thermal';
                          return (
                            <Card
                              key={`quotation-${tpl.id}`}
                              variant={selected ? 'outlined' : 'elevation'}
                              onClick={() => handleConfigChange('quotationPrintTemplate', tpl.id)}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                borderColor: selected ? 'primary.main' : 'divider',
                                boxShadow: selected ? 4 : 1,
                                minWidth: 220,
                                maxWidth: 260,
                              }}
                            >
                              <CardContent sx={{ py: 1.5, px: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: selected ? 700 : 500, mb: 0.5 }}
                                  color={selected ? 'primary.main' : 'text.primary'}
                                >
                                  {tpl.label}
                                </Typography>
                                <Box
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    height: 70,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: 10,
                                      bgcolor: 'grey.300',
                                      borderRadius: 0.5,
                                      mb: 0.5,
                                      width: isThermal ? '70%' : '50%',
                                      mx: 'auto',
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: isThermal ? 'column' : 'row',
                                      gap: 0.4,
                                      flex: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        flex: isThermal ? 'none' : 1,
                                        height: isThermal ? 8 : '100%',
                                        bgcolor: 'grey.200',
                                        borderRadius: 0.5,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        flex: 1,
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.3,
                                      }}
                                    >
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 0.5 }} />
                                      <Box sx={{ height: 6, bgcolor: 'grey.200', borderRadius: 0.5 }} />
                                    </Box>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {tpl.description}
                                </Typography>
                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<ImageIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openTemplatePreview(tpl.id, 'quotation');
                                    }}
                                  >
                                    Preview
                                  </Button>
                                </Box>
                                {selected && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      size="small"
                                      color="primary"
                                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                      label="Selected"
                                    />
                                  </Box>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {saveSuccess && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="success" onClose={() => setSaveSuccess(false)}>
                      Configuration saved successfully.
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleSaveConfiguration}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Printer Settings'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 5 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              System Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemSettings.enableNotifications}
                      onChange={(e) =>
                        setSystemSettings({ ...systemSettings, enableNotifications: e.target.checked })
                      }
                    />
                  }
                  label="Enable System Notifications"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={systemSettings.enableEmailAlerts}
                      onChange={(e) =>
                        setSystemSettings({ ...systemSettings, enableEmailAlerts: e.target.checked })
                      }
                    />
                  }
                  label="Enable Email Alerts"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Low Stock Threshold"
                  value={systemSettings.lowStockThreshold}
                  onChange={(e) =>
                    setSystemSettings({ ...systemSettings, lowStockThreshold: parseInt(e.target.value) || 0 })
                  }
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  select
                  label="Stock Valuation Method"
                  value={systemSettings.stockValuationMethod || 'LPC'}
                  onChange={(e) =>
                    setSystemSettings({ ...systemSettings, stockValuationMethod: e.target.value })
                  }
                  helperText="Default method used for inventory valuation"
                >
                  <MenuItem value="LPC">Last Purchase Cost (LPC) - Default</MenuItem>
                  <MenuItem value="FIFO">First In, First Out (FIFO)</MenuItem>
                  <MenuItem value="AWC">Average Weighted Cost (AWC)</MenuItem>
                </TextField>
              </Grid>
              {systemSaveSuccess && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success" onClose={() => setSystemSaveSuccess(false)}>
                    System settings saved successfully.
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSaveSystemSettings}
                  disabled={!canUpdateSystemSettings || saving}
                >
                  {saving ? 'Saving...' : 'Save System Settings'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {tabValue === 6 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Roles & Permission Matrix
                </Typography>
                <Button variant="contained" size="small" onClick={() => navigate('/roles')} startIcon={<SecurityIcon />} sx={{ borderRadius: 2 }}>
                  Manage Roles
                </Button>
              </Box>
              {!roles || !allPermissions ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
              ) : (
                <PermissionMatrix roles={roles} allPermissions={allPermissions} />
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {tabValue === 7 && (
        <MenuOrderCustomizer company={company} />
      )}
    </Box>
  );
}

function PermissionMatrix({ roles, allPermissions }) {
  const moduleGroups = useMemo(() => {
    const groups = {};
    (allPermissions || []).forEach((p) => {
      const key = `${p.module} / ${p.resource}`;
      if (!groups[key]) groups[key] = { module: p.module, resource: p.resource, perms: {} };
      groups[key].perms[p.action] = p.id;
    });
    return groups;
  }, [allPermissions]);

  const actions = ['read', 'create', 'update', 'delete', 'insufficient_stock'];
  const actionLabels = { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete', insufficient_stock: 'Sell Insufficient Stock' };

  const rolePermSets = useMemo(() => {
    const map = {};
    (roles || []).forEach((r) => {
      map[r.id] = new Set((r.permissions || []).map((p) => p.id));
    });
    return map;
  }, [roles]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 600 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 180, bgcolor: 'grey.50', position: 'sticky', left: 0, zIndex: 3 }}>Module / Resource</TableCell>
            <TableCell sx={{ fontWeight: 700, width: 80, bgcolor: 'grey.50' }}>Action</TableCell>
            {(roles || []).map((r) => (
              <TableCell key={r.id} align="center" sx={{ fontWeight: 700, bgcolor: 'grey.50', minWidth: 100 }}>
                {r.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(moduleGroups).map(([groupKey, group]) =>
            actions
              .filter((a) => group.perms[a])
              .map((action, idx) => (
                <TableRow key={`${groupKey}-${action}`} hover>
                  {idx === 0 && (
                    <TableCell
                      rowSpan={actions.filter((a) => group.perms[a]).length}
                      sx={{ fontWeight: 500, textTransform: 'capitalize', verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}
                    >
                      {groupKey.replace(/-/g, ' ')}
                    </TableCell>
                  )}
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    <Chip
                      label={actionLabels[action]}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, minWidth: 55 }}
                      color={
                        action === 'read' ? 'info' :
                        action === 'create' ? 'success' :
                        action === 'update' ? 'warning' :
                        action === 'delete' ? 'error' :
                        'secondary'
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  {(roles || []).map((r) => {
                    const permId = group.perms[action];
                    const has = rolePermSets[r.id]?.has(permId);
                    return (
                      <TableCell key={r.id} align="center">
                        {has ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CancelIcon sx={{ color: 'grey.300', fontSize: 20 }} />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EmailTemplatesList() {
  const { data: templates, isLoading } = useGetEmailTemplatesQuery();
  const [deleteTemplate] = useDeleteEmailTemplateMutation();
  const navigate = useNavigate();

  if (isLoading) return <CircularProgress size={24} />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead sx={{ bgcolor: 'grey.50' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Default</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                No templates created yet.
              </TableCell>
            </TableRow>
          ) : (
            templates?.map((t) => (
              <TableRow key={t.id}>
                <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                <TableCell>
                  <Chip label={t.type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{t.subject}</TableCell>
                <TableCell>
                  {t.isDefault ? <Chip label="Default" size="small" color="success" /> : '-'}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => navigate(`/settings/email-templates/edit/${t.id}`)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to delete this template?')) {
                          try {
                            await deleteTemplate(t.id).unwrap();
                          } catch (err) {
                            alert('Failed to delete template');
                          }
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default Settings;

